import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { msalConfig } from './config/authConfig';
import { AuthProvider, useAuth } from './context/AuthContext';
import Navbar from './components/Navbar';
import ProtectedRoute from './components/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import HRHub from './pages/HRHub';
import PublicTrainingVideos from './pages/PublicTrainingVideos';
import PolicyFileRedirect from './pages/PolicyFileRedirect';
import AdminPanel from './pages/admin/AdminPanel';
import UserManagement from './pages/admin/UserManagement';
import PolicyManagement from './pages/admin/PolicyManagement';
import VideoManagement from './pages/admin/VideoManagement';
import TagManagement from './pages/admin/TagManagement';
import ActivityLogs from './pages/admin/ActivityLogs';
import ApiTokenManagement from './pages/admin/ApiTokenManagement';
import AcknowledgementManagement from './pages/admin/AcknowledgementManagement';
import './App.css';

const msalInstance = new PublicClientApplication(msalConfig);

async function initializeMsal() {
  await msalInstance.initialize();

  await msalInstance.handleRedirectPromise().catch((error) => {
    console.error('Redirect error:', error);
  });

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
  }

  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {
      msalInstance.setActiveAccount(event.payload.account);
    }
  });
}

function AppLayout({ children }) {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  // The HR Hub ('/') ships its own full-height sidebar shell, so the
  // top navbar is suppressed there to match the approved design.
  const hideNavbar = location.pathname === '/' || location.pathname === '/training-videos';

  if (!isAuthenticated || hideNavbar) {
    return children;
  }

  return (
    <>
      <Navbar />
      {children}
    </>
  );
}

function AppRoutes() {
  return (
    <AppLayout>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/training-videos"
          element={
            <ProtectedRoute>
              <PublicTrainingVideos />
            </ProtectedRoute>
          }
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <HRHub />
            </ProtectedRoute>
          }
        />
        <Route
          path="/policies/:id"
          element={
            <ProtectedRoute>
              <PolicyFileRedirect />
            </ProtectedRoute>
          }
        />
        {/* Videos are now part of the HR Hub; keep the old path working. */}
        <Route path="/videos" element={<Navigate to="/" replace />} />
        <Route
          path="/admin"
          element={
            <ProtectedRoute requireAdmin>
              <AdminPanel />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="users" replace />} />
          <Route path="users" element={<UserManagement />} />
          <Route path="policies" element={<PolicyManagement />} />
          <Route path="videos" element={<VideoManagement />} />
          <Route path="tags" element={<TagManagement />} />
          <Route path="acknowledgements" element={<AcknowledgementManagement />} />
          <Route path="api-tokens" element={<ApiTokenManagement />} />
          <Route path="logs" element={<ActivityLogs />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppLayout>
  );
}

function App() {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    initializeMsal().then(() => {
      setIsInitialized(true);
    });
  }, []);

  if (!isInitialized) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <img src="/logo.png" alt="i2e Consulting" className="h-12 w-auto mx-auto mb-4 animate-pulse" />
          <p className="text-gray-500">Initializing...</p>
        </div>
      </div>
    );
  }

  return (
    <MsalProvider instance={msalInstance}>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </MsalProvider>
  );
}

export default App;
